import { FormattedMessage } from "react-intl";

import {
  FunctionalCard,
  FunctionalCardContent,
} from "@/components/molecules/cards/FunctionalCard";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/molecules/Popover";
import QuestionFilled from "@/icons/__generated__/QuestionFilled/QuestionFilled16";
import { IFaqs } from "~/types/api-types";
import { IMessageDescriptor } from "~/types/local-types";

import {
  miscEventFaqDefinitions,
  EVENT_FAQ_CONST,
  FOOD_BEVERAGE_PROPS,
  ALCOHOL_PROPS,
  FURNITURE_PROPS,
  RESTROOM_PROPS,
  AV_MUSIC_PROPS,
  EVENT_ACTIVITIES_PROPS,
  EVENT_RULES_PROPS,
} from "./event-faq-definitions";
import { isKnownKey } from "./typeguards";

const amenityMapping: Record<string, string> = {
  standard_restrooms: "restroom",
  cups_glasses: "cups",
  place_setting: "place_setting",
};

function getTooltipForAmenity(amenity: string) {
  // Map the amenity string if a mapping exists
  const mappedAmenity = amenityMapping[amenity] || amenity;

  // Generate the tooltip key
  const tooltipKey = `${mappedAmenity}_tooltip`;

  // Check if the key exists in the hardcoded list
  if (isKnownKey(tooltipKey, miscEventFaqDefinitions)) {
    return miscEventFaqDefinitions[tooltipKey];
  }

  return undefined;
}

const renderEntryTitle = (
  messagedObj: IMessageDescriptor,
  quantity?: number,
  amenity?: string,
) => {
  const toolTipAvailable = getTooltipForAmenity(amenity || "");
  return (
    <div className="faq-label tw-flex tw-items-center">
      <div className={quantity ? "amenity-description ellipsis" : ""}>
        <FormattedMessage {...messagedObj} />
      </div>
      {quantity && (
        <>
          <span className="tw-text-gray-900 tw-ml-4 tw-mr-4 tw-text-xs">•</span>
          <span>Qty: {quantity}</span>
        </>
      )}
      {toolTipAvailable && (
        <Popover>
          <PopoverTrigger className="tw-ml-2">
            <QuestionFilled className="tw-text-gray-900" />
          </PopoverTrigger>
          <PopoverContent
            align="center"
            className="tw-bg-base-white tw-w-[300px] tw-p-16 tw-z-50"
          >
            <FunctionalCard className="tw-border-none">
              <FunctionalCardContent className="tw-p-0">
                <FormattedMessage {...toolTipAvailable} />
              </FunctionalCardContent>
            </FunctionalCard>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

const alcoholRules = (data: IFaqs) => {
  const alcoholRuleArr = [
    "alcohol_must_be_served_by_licensed_vendor",
    "can_purchase_alcohol_at_full_service_bar",
    "can_purchase_beer_wine_at_bar",
  ];

  interface AlcoholRuleOptions {
    alcohol_must_be_served_by_licensed_vendor: string;
    can_purchase_alcohol_at_full_service_bar: string;
    can_purchase_beer_wine_at_bar: string;
  }
  const alcoholRule =
    "bring_alcohol" in data ? data["bring_alcohol"] : "bring_alcohol_allowed";
  let moreInformation: string | string[] = [];
  let crossedOutRules: string | string[] = [];
  if ("bring_alcohol_allowed" === alcoholRule) {
    moreInformation =
      "bring_alcohol_rules" in data && data["bring_alcohol_rules"]
        ? data["bring_alcohol_rules"]
        : "any_alcohol";
  } else if ("bring_alcohol_not_allowed" === alcoholRule) {
    moreInformation = alcoholRuleArr.filter(
      (rule) => rule in data && data[rule as keyof AlcoholRuleOptions],
    );
    crossedOutRules = alcoholRuleArr.filter(
      (rule) => rule in data && !data[rule as keyof AlcoholRuleOptions],
    );
  }
  return {
    alcoholRule,
    moreInformation,
    crossedOutRules,
  };
};

const AVOptionsSelected = (data: IFaqs) => {
  const allAVOptions = ["microphones", "projector", "speakers", "television"];
  return allAVOptions.filter((activity) => activity in data);
};

const AVRulesSelected = (data: IFaqs) => {
  const allAVRules = [
    "is_dj_allowed",
    "is_live_music_allowed",
    "is_amplified_music_allowed",
    "is_connected_amplified_music_allowed",
  ];

  interface AVEquipmentRuleOptions {
    is_dj_allowed: string;
    is_live_music_allowed: string;
    is_amplified_music_allowed: string;
    is_connected_amplified_music_allowed: string;
  }
  return allAVRules.filter(
    (activity) =>
      activity in data && data[activity as keyof AVEquipmentRuleOptions],
  );
};

const AVRulesExplicitlyProhibited = (data: IFaqs) => {
  const allAVRules = [
    "is_dj_allowed",
    "is_live_music_allowed",
    "is_amplified_music_allowed",
    "is_connected_amplified_music_allowed",
  ];
  interface AVEquipmentRuleOptions {
    is_dj_allowed: string;
    is_live_music_allowed: string;
    is_amplified_music_allowed: string;
    is_connected_amplified_music_allowed: string;
  }
  return allAVRules.filter(
    (activity) =>
      activity in data && !data[activity as keyof AVEquipmentRuleOptions],
  );
};

const eventActivitiesSelected = (data: IFaqs) => {
  const {
    ARCADE_GAMES,
    DARTS,
    KARAOKE,
    PING_PONG_TABLE,
    POOL_TABLE,
    SHUFFLEBOARD,
  } = EVENT_FAQ_CONST;
  const allActivities = [
    ARCADE_GAMES,
    DARTS,
    KARAOKE,
    PING_PONG_TABLE,
    POOL_TABLE,
    SHUFFLEBOARD,
  ];
  return allActivities.filter((activity) => activity in data);
};

const eventRulesSelected = (data: IFaqs) => {
  const allEventRules = [
    "is_glitter_allowed",
    "is_hired_security_required",
    "are_open_flames_allowed",
    "are_pets_allowed",
    "is_smoking_allowed",
    "are_ticketed_events_allowed",
  ];

  interface EventRuleOptions {
    is_glitter_allowed: string;
    is_hired_security_required: string;
    are_open_flames_allowed: string;
    are_pets_allowed: string;
    is_smoking_allowed: string;
    are_ticketed_events_allowed: string;
  }
  return allEventRules.filter(
    (rule) => rule in data && data[rule as keyof EventRuleOptions],
  );
};

const eventRulesExplicitlyProhibited = (data: IFaqs) => {
  const allEventRules = [
    "is_glitter_allowed",
    "is_hired_security_required",
    "are_open_flames_allowed",
    "are_pets_allowed",
    "is_smoking_allowed",
    "are_ticketed_events_allowed",
  ];

  interface EventRuleOptions {
    is_glitter_allowed: string;
    is_hired_security_required: string;
    are_open_flames_allowed: string;
    are_pets_allowed: string;
    is_smoking_allowed: string;
    are_ticketed_events_allowed: string;
  }
  return allEventRules.filter(
    (rule) => rule in data && !data[rule as keyof EventRuleOptions],
  );
};

const restroomsSelected = (data: IFaqs) => {
  const restroomOptions = [
    "standard_restrooms",
    "ada_restrooms",
    "nursing_rooms",
  ];
  return {
    selectedRestrooms: restroomOptions.filter((restroom) => restroom in data),
    notSelectedRestrooms: restroomOptions.filter(
      (restroom) => !(restroom in data),
    ),
  };
};

const kitchensSelected = (data: IFaqs) => {
  const kitchenSetups = [
    "full_kitchen",
    "commercial_kitchen",
    "kitchenette",
    "prep_area",
  ];
  return kitchenSetups.filter((kitchen) => kitchen in data);
};

const furnitureSelected = (data: IFaqs) => {
  const furnitureTypes = ["tables", "chairs", "soft_seating"];
  return furnitureTypes.filter((furniture) => furniture in data);
};

const activitiesNotAllowed = (data: IFaqs) => {
  const allActivitiesNotAllowed = [
    "is_21st_bday_prohibited",
    "is_after_party_prohibited",
    "is_album_release_party_prohibited",
    "is_bachelor_party_prohibited",
    "is_concert_prohibited",
    "is_festival_holiday_party_prohibited",
    "is_influencer_event_prohibited",
    "is_kids_party_prohibited",
    "is_sports_viewing_party_prohibited",
    "is_party_prohibited",
    "is_wedding_prohibited",
  ];

  interface ProhibitedActivityOptions {
    is_21st_bday_prohibited: string;
    is_after_party_prohibited: string;
    is_album_release_party_prohibited: string;
    is_bachelor_party_prohibited: string;
    is_concert_prohibited: string;
    is_festival_holiday_party_prohibited: string;
    is_influencer_event_prohibited: string;
    is_kids_party_prohibited: string;
    is_sports_viewing_party_prohibited: string;
    is_party_prohibited: string;
    is_wedding_prohibited: string;
  }
  return allActivitiesNotAllowed.filter(
    (activity) =>
      activity in data && data[activity as keyof ProhibitedActivityOptions],
  );
};

const hasValidMiscActivities = (data: IFaqs) => {
  const miscActivities = data[EVENT_FAQ_CONST.MISC_ACTIVITIES] as {
    description?: string;
  };
  return miscActivities?.description && miscActivities.description !== "";
};

const displaySections = {
  displayFoodBeverageSection: (data: IFaqs) =>
    FOOD_BEVERAGE_PROPS.some((key) => key in data),
  displayAlcoholSection: (data: IFaqs) =>
    ALCOHOL_PROPS.some((key) => key in data),
  displayFurnitureSection: (data: IFaqs) =>
    FURNITURE_PROPS.some((key) => key in data),
  displayRestroomSection: (data: IFaqs) =>
    RESTROOM_PROPS.some((key) => key in data),
  displayAVMusicSection: (data: IFaqs) =>
    AV_MUSIC_PROPS.some((key) => key in data && data[key]),
  displayEventActivitiesSection: (data: IFaqs) =>
    EVENT_ACTIVITIES_PROPS.filter(
      (key) => key !== EVENT_FAQ_CONST.MISC_ACTIVITIES,
    ).some((key) => key in data && data[key]) || hasValidMiscActivities(data),
  displayEventRulesSection: (data: IFaqs) =>
    EVENT_RULES_PROPS.some((key) => key in data),
};

export {
  renderEntryTitle,
  alcoholRules,
  eventActivitiesSelected,
  activitiesNotAllowed,
  AVOptionsSelected,
  AVRulesSelected,
  AVRulesExplicitlyProhibited,
  eventRulesSelected,
  eventRulesExplicitlyProhibited,
  restroomsSelected,
  kitchensSelected,
  furnitureSelected,
  displaySections,
};
